import React, { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { CalendarClock, Plus, Play, Pencil, Trash2, X, CreditCard, Loader2 } from 'lucide-react';
import { apiServicePlans, apiCustomers, servicePlans } from '../../lib/api';
import { toast } from 'sonner';
import { todayISO } from '../../lib/dates';

const money = (n) => (Number(n) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const FREQS = ['Weekly', 'Bi-Weekly', 'Monthly', 'Bi-Monthly', 'Quarterly', 'Semi-Annual', 'Annual'];

const blankForm = () => ({
  customerId: '', customerName: '', customerAccountNumber: '', address: '',
  service: '', amount: '', frequency: 'Quarterly', startDate: todayISO(),
  autopay: false, createInvoice: true, active: true,
});

export default function ServicePlansPage() {
  const [plans, setPlans] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [editing, setEditing] = useState(null); // null | 'new' | planId
  const [form, setForm] = useState(blankForm());
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([apiServicePlans.list(), apiCustomers.list()]);
      setPlans(Array.isArray(p) ? p : []);
      setCustomers(Array.isArray(c) ? c : []);
    } catch (e) {
      toast.error('Could not load recurring plans');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(blankForm()); setEditing('new'); };
  const openEdit = (p) => {
    setForm({
      customerId: p.customerId || '', customerName: p.customerName || '',
      customerAccountNumber: p.customerAccountNumber || '', address: p.address || '',
      service: p.service || '', amount: p.amount ?? '', frequency: p.frequency || 'Quarterly',
      startDate: p.startDate || todayISO(), autopay: !!p.autopay,
      createInvoice: p.createInvoice !== false, active: p.active !== false,
    });
    setEditing(p.id);
  };
  const close = () => { setEditing(null); setForm(blankForm()); };

  const onCustomer = (id) => {
    const c = customers.find((x) => x.id === id);
    setForm((f) => ({
      ...f, customerId: id,
      customerName: c ? (c.name || c.customerName || '') : '',
      customerAccountNumber: c ? (c.accountNumber || '') : '',
      address: c ? (c.address || c.serviceAddress || '') : '',
    }));
  };

  const save = async () => {
    if (!form.customerId) { toast.error('Pick a customer'); return; }
    if (!form.service) { toast.error('Enter a service description'); return; }
    setSaving(true);
    try {
      const payload = { ...form, amount: Number(form.amount) || 0, nextRunDate: form.startDate };
      if (editing === 'new') {
        await apiServicePlans.create(payload);
        toast.success('Recurring plan created');
      } else {
        const existing = plans.find((p) => p.id === editing) || {};
        await apiServicePlans.update(editing, { ...existing, ...payload, nextRunDate: existing.nextRunDate || form.startDate });
        toast.success('Plan updated');
      }
      close(); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save plan');
    } finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete the recurring plan for ${p.customerName || 'this customer'}?`)) return;
    try { await apiServicePlans.remove(p.id); toast.success('Plan deleted'); load(); }
    catch { toast.error('Could not delete plan'); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await servicePlans.run();
      toast.success(`Generated ${r.workOrdersCreated} work order(s) and ${r.invoicesCreated} invoice(s).`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not run plans');
    } finally { setRunning(false); }
  };

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <CalendarClock className="w-6 h-6 text-[#8BC53F]" />
          <h1 className="text-2xl font-extrabold text-slate-900">Recurring Plans</h1>
        </div>
        <div className="flex gap-2">
          <Button onClick={runNow} disabled={running} variant="outline" className="gap-1.5" data-testid="run-plans-btn">
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Run due plans
          </Button>
          <Button onClick={openNew} className="bg-[#8BC53F] hover:bg-[#6FA82C] text-white font-semibold gap-1.5" data-testid="new-plan-btn">
            <Plus className="w-4 h-4" /> New Plan
          </Button>
        </div>
      </div>
      <p className="text-slate-500 text-sm mt-1">Automatically create work orders and invoices on a schedule — quarterly, monthly, and more.</p>

      {editing && (
        <Card className="mt-5 p-5 border-slate-200" data-testid="plan-editor">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-slate-900">{editing === 'new' ? 'New recurring plan' : 'Edit plan'}</h2>
            <button onClick={close} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Customer</label>
              <select value={form.customerId} onChange={(e) => onCustomer(e.target.value)} className="mt-1 w-full h-11 rounded-lg border border-slate-200 px-3 bg-white">
                <option value="">Select a customer…</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name || c.customerName || c.id}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Service</label>
              <Input value={form.service} onChange={(e) => setForm((f) => ({ ...f, service: e.target.value }))} placeholder="Quarterly Pest Treatment" className="mt-1 h-11" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Amount per visit ($)</label>
              <Input type="number" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} placeholder="149" className="mt-1 h-11" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Frequency</label>
              <select value={form.frequency} onChange={(e) => setForm((f) => ({ ...f, frequency: e.target.value }))} className="mt-1 w-full h-11 rounded-lg border border-slate-200 px-3 bg-white">
                {FREQS.map((fr) => <option key={fr} value={fr}>{fr}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Start date</label>
              <Input type="date" value={form.startDate} onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))} className="mt-1 h-11" />
            </div>
            <div className="flex flex-col justify-center gap-2 pt-2">
              <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={form.createInvoice} onChange={(e) => setForm((f) => ({ ...f, createInvoice: e.target.checked }))} /> Auto-create invoice each visit</label>
              <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={form.autopay} onChange={(e) => setForm((f) => ({ ...f, autopay: e.target.checked }))} /> Autopay (charge saved card)</label>
              <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} /> Active</label>
            </div>
          </div>
          {form.autopay && <p className="text-xs text-amber-600 mt-3">Autopay records intent now. Card-on-file charging activates once Stripe live keys and saved cards are configured.</p>}
          <div className="flex gap-2 mt-5">
            <Button onClick={save} disabled={saving} className="bg-[#8BC53F] hover:bg-[#6FA82C] text-white font-semibold">{saving ? 'Saving…' : 'Save plan'}</Button>
            <Button onClick={close} variant="outline">Cancel</Button>
          </div>
        </Card>
      )}

      <div className="mt-5">
        {loading ? (
          <div className="py-12 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : plans.length === 0 ? (
          <Card className="p-10 text-center border-dashed border-slate-200" data-testid="no-plans">
            <CalendarClock className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-slate-500">No recurring plans yet. Create one to automate repeat billing.</p>
          </Card>
        ) : (
          <div className="space-y-2">
            {plans.map((p) => (
              <Card key={p.id} className="p-4 border-slate-200 flex items-center gap-4 flex-wrap" data-testid="plan-row">
                <div className="flex-1 min-w-[160px]">
                  <div className="font-bold text-slate-900">{p.customerName || '—'}</div>
                  <div className="text-sm text-slate-500">{p.service || 'Service'}</div>
                </div>
                <div className="text-sm"><span className="text-slate-400">Every</span> <b>{p.frequency || 'Monthly'}</b></div>
                <div className="text-sm"><span className="text-slate-400">Next</span> <b>{p.nextRunDate || p.startDate || '—'}</b></div>
                <div className="font-extrabold text-[#6FA82C]">{money(p.amount)}</div>
                {p.autopay && <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full"><CreditCard className="w-3 h-3" />Autopay</span>}
                <span className={`text-xs px-2 py-1 rounded-full ${p.active !== false ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{p.active !== false ? 'Active' : 'Paused'}</span>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(p)} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500" title="Edit"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(p)} className="w-8 h-8 rounded-lg hover:bg-red-50 flex items-center justify-center text-red-500" title="Delete"><Trash2 className="w-4 h-4" /></button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
